/**
 * How far a campaign has got, and what happened to everybody it did not send to.
 *
 * Three things here are less obvious than they look.
 *
 * **The bar counts skipped recipients as done.** A bar drawn as `sent / total`
 * never reaches 100% when anything was skipped, and in a guarded environment
 * nearly everything is skipped — so a perfectly successful send would sit at
 * 0.2% and look hung. A skipped recipient is *resolved*: nothing more will
 * happen to them. `campaignProgressReading` in `@wend/shared` is what decides
 * that, and this panel draws what it returns rather than dividing two numbers of
 * its own.
 *
 * **"1,278 skipped" is not an explanation.** It is the shape of one, and on its
 * own it is exactly the number that makes a person believe the product is
 * broken. So the skips are broken down by reason, every reason carries the
 * sentence the contract wrote for it, and each line links to the recipients it
 * is about — because the next question is always "which ones?".
 *
 * **"Bounced" is an Amazon word and the panel takes a channel to keep it off
 * WhatsApp.** The counter was already hidden on a WhatsApp campaign, but only
 * because `counts.bounced` happens to be zero there — a product rule held up by
 * the data rather than by the screen. `isBounceMeasured` is what holds it now,
 * which is the same predicate `CampaignResultsPanel` branches on one card down,
 * so the two cannot come to different conclusions about whether this channel has
 * bounces. The channel is a required prop for that one use: an optional one
 * would default to showing the Amazon word.
 *
 * Every figure is the API's, computed on read from `campaign_recipients` on the
 * request that drew this. Nothing here is incremented, predicted or remembered.
 *
 * **Slice 17 shortened two sentences and deleted none.** The "these are absences,
 * not zeros" line on an unscheduled campaign and the "skipped counts as resolved"
 * line under the bar are both the second kind of text in that slice's rule —
 * remove either and a reader draws a false conclusion from a number in front of
 * them — so they stayed, at about half their length. The clause naming what
 * scheduling does stayed too, in five words, because a person reading "no
 * recipient list yet" with no idea what produces one has been told half a fact;
 * what went was its elaboration, which the schedule panel owns and states with
 * this campaign's own numbers in it.
 *
 * **Slice 66 put the bar in a figure panel.** The percentage was a 2.1rem mono
 * numeral painted in the brand gradient as text, over a hand-rolled gradient
 * bar. Two suites count that gradient class as "the primary action on this
 * screen", and a progress bar is not an action, so the
 * figure is now the shared `MetricPanel` — sans tabular digits, the kicker
 * role, and the `Meter` (the brand sweep, which nothing counts) under it. The
 * resolved-of-total sentence is the panel's `sub` and keeps `role="status"`,
 * so a screen reader still hears the count move. The counters below are the
 * shared `Counter` from `CampaignFailureSections`, not a copy of it.
 */
import {
  campaignProgressReading,
  isBounceMeasured,
  type CampaignProgress,
  type CampaignSkipReason,
  type Channel,
} from '@wend/shared';
import { Badge, Button, Kicker, MetricPanel } from '@wend/ui';
import { ListFilter } from 'lucide-react';

import { skipReasonRows } from '../../lib/campaign-display';
import { formatCount, pluralise } from '../../lib/format';
import { Counter } from './CampaignFailureSections';

export interface CampaignProgressPanelProps {
  progress: CampaignProgress;
  /**
   * The campaign's channel, for one purpose: **"Bounced" is an email word.**
   *
   * See the counters below. Nothing else on this panel branches on it, and it is
   * required rather than optional so that a caller cannot forget it.
   */
  channel: Channel;
  /** Focuses the recipient list on one reason — "show me who the guard blocked". */
  onShowSkipReason: (reason: CampaignSkipReason) => void;
}

export function CampaignProgressPanel({
  progress,
  channel,
  onShowSkipReason,
}: CampaignProgressPanelProps) {
  const reading = campaignProgressReading(progress);
  const counts = progress.counts;

  if (reading.notMaterialised) {
    return (
      <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
        No recipient list yet, so there is nothing to report — these are absences, not zeros.
        Scheduling builds the list, and works out who will be skipped and why.
      </p>
    );
  }

  const skips = skipReasonRows(progress.skippedByReason);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2.5">
        {/* The meter is decoration for the number written out above it. */}
        <MetricPanel
          className="max-w-xl"
          kicker={reading.complete ? 'Resolved' : 'Resolved so far'}
          figure={{ kind: 'measured', value: reading.percentComplete, unit: '%' }}
          figureSize="lg"
          sub={
            <span role="status">
              {reading.complete
                ? `All ${pluralise(reading.total, 'recipient')} resolved`
                : `${formatCount(reading.resolved)} of ${formatCount(reading.total)} recipients resolved · ${formatCount(reading.remaining)} still waiting`}
            </span>
          }
          meter={{ value: reading.percentComplete / 100 }}
        />

        <p className="max-w-prose text-[0.82rem] leading-relaxed text-muted">
          Skipped counts as resolved: nothing further happens to them. Of{' '}
          {pluralise(reading.total, 'recipient')} on this list, {formatCount(reading.sendable)} will
          be sent to.
        </p>
      </div>

      <dl className="flex flex-wrap gap-2.5">
        <Counter label="Waiting" value={counts.pending} />
        <Counter label="Sent" value={counts.sent} tone="text-text" />
        <Counter label="Skipped" value={counts.skipped} />
        <Counter label="Failed" value={counts.failed} tone={counts.failed > 0 ? 'text-bad' : undefined} />
        {/*
         * A zero here would be read as "nothing was delivered", and until a
         * provider has answered for a message that is not what is true — it is
         * "nothing has come back yet". So neither counter appears until it has
         * something to report. That was written when no delivery event existed
         * at all; both channels report deliveries since slice 12, and the rule
         * survives the change unaltered because it was never about the channel.
         *
         * BOUNCED IS GATED ON THE CHANNEL AS WELL, and not because the number
         * would be wrong. WhatsApp has no bounce, so `counts.bounced` is zero on
         * every WhatsApp campaign and the `> 0` test hides the counter already —
         * which means the product rule is being enforced by *data* rather than
         * by the screen. `isBounceMeasured` makes it structural, so the day a
         * miscounted event or a third channel puts a number in that field, this
         * panel still does not print an Amazon word on a Meta campaign.
         */}
        {counts.delivered > 0 && <Counter label="Delivered" value={counts.delivered} tone="text-good" />}
        {isBounceMeasured(channel) && counts.bounced > 0 && (
          <Counter label="Bounced" value={counts.bounced} tone="text-bad" />
        )}
      </dl>

      {counts.skipped > 0 && (
        <div className="flex flex-col gap-3 pt-2">
          <Kicker rule>
            Why {formatCount(counts.skipped)} received nothing
          </Kicker>

          <ul className="flex flex-col">
            {skips.map((row) => (
              <li
                key={row.reason}
                className="flex flex-col gap-2 border-t border-line py-3.5 first:border-t-0 first:pt-0"
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="text-[1.05rem] font-semibold tabular-nums text-text">
                    {formatCount(row.count)}
                  </span>
                  <Badge tone={row.tone}>{row.label}</Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    onClick={() => {
                      onShowSkipReason(row.reason);
                    }}
                  >
                    <ListFilter aria-hidden="true" className="size-4" />
                    Show these
                  </Button>
                </div>
                <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
                  {row.sentence}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

